import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { DuplicateCardsHelpers } from 'ontraccr-common';
import { searchBoardDuplicates, clearDuplicateBoardSearchResults } from '../state/forms.actions';
import Debouncer from '../../helpers/Debouncer';

const {
  formatFieldsForOpenSearch,
  getAllFilledFieldsToDetect,
  getFieldsToDetect,
  getFieldsToDetectFieldValues,
  getConfigureSearchInitialQuery: buildConfigureSearchInitialQuery,
} = DuplicateCardsHelpers;

export default function useDuplicateCardDetection({
  formData,
  responses,
  visible = false,
  isConfigureSearchActive = false,
}) {
  const dispatch = useDispatch();
  const debouncerRef = useRef(new Debouncer());

  const duplicateBoardSearchResults = useSelector(
    (state) => state.forms.duplicateBoardSearchResults,
  );

  const detectBoardCardConfig = useMemo(() => {
    const boardCardNode = formData?.workflow?.nodes?.find((node) => (
      node.type === 'createBoardCard' && node?.data?.detectDuplicates
    ));

    if (!boardCardNode) return null;

    const { data = {} } = boardCardNode;

    return {
      detectDuplicates: data.detectDuplicates,
      checkAllBoardsWithSameCardType: data.checkAllBoardsWithSameCardType,
      allowConfigureConflictDetection: !!data.allowConfigureConflictDetection,
      board: data.board,
      status: data.status,
      fieldMappings: data.fieldMappings,
      cardTypeId: data.cardTypeId,
      useToDetect: data.useToDetect ?? {},
      showInDetection: data.showInDetection ?? {},
    };
  }, [formData]);

  const allUseToDetectFieldsFilled = useMemo(() => {
    if (!detectBoardCardConfig) return false;

    const { fieldMappings, detectDuplicates } = detectBoardCardConfig;

    if (!detectDuplicates) return false;

    const fieldsToDetect = getFieldsToDetect(detectBoardCardConfig);

    if (fieldsToDetect.length === 0) return false;

    return getAllFilledFieldsToDetect(
      fieldsToDetect,
      fieldMappings,
      responses ?? {},
    );
  }, [detectBoardCardConfig, responses]);

  const buildDefaultSearchQuery = useCallback(() => {
    if (!detectBoardCardConfig || !allUseToDetectFieldsFilled) return null;

    const fieldsToDetect = getFieldsToDetect(detectBoardCardConfig);

    const fieldsToDetectFieldValues = getFieldsToDetectFieldValues(
      allUseToDetectFieldsFilled,
      fieldsToDetect,
      detectBoardCardConfig,
      responses ?? {},
    );

    const openSearchQuery = formatFieldsForOpenSearch({
      fields: fieldsToDetectFieldValues,
      allUseToDetectFieldsFilled,
      boardCardConfig: detectBoardCardConfig,
      responses,
    });

    const checkAllBoardsWithSameCardType = !!detectBoardCardConfig?.checkAllBoardsWithSameCardType;
    const canSearch = checkAllBoardsWithSameCardType
      ? openSearchQuery.cardTypeId
      : openSearchQuery?.boardIds?.length;

    if (!openSearchQuery || !canSearch) return null;

    return { ...openSearchQuery, checkAllBoardsWithSameCardType };
  }, [allUseToDetectFieldsFilled, detectBoardCardConfig, responses]);

  const getConfigureSearchInitialQuery = useCallback(() => (
    buildConfigureSearchInitialQuery(detectBoardCardConfig, responses ?? {})
  ), [detectBoardCardConfig, responses]);

  const searchBoardsForDuplicates = useCallback(async () => {
    const query = buildDefaultSearchQuery();
    if (!query) return;

    await dispatch(searchBoardDuplicates(
      query,
      detectBoardCardConfig?.showInDetection,
    ));
  }, [buildDefaultSearchQuery, detectBoardCardConfig]);

  useEffect(() => {
    const debouncer = debouncerRef.current;
    if (!allUseToDetectFieldsFilled || !visible) {
      debouncer.clear();
      if (!isConfigureSearchActive) {
        dispatch(clearDuplicateBoardSearchResults());
      }
      return;
    }
    if (isConfigureSearchActive) {
      debouncer.clear();
      return;
    }
    debouncer.debounce(() => searchBoardsForDuplicates(), 300);
  }, [
    allUseToDetectFieldsFilled,
    searchBoardsForDuplicates,
    visible,
    isConfigureSearchActive,
  ]);

  // Clear results when component unmounts or becomes invisible
  useEffect(() => {
    if (!visible && !isConfigureSearchActive) {
      dispatch(clearDuplicateBoardSearchResults());
    }
  }, [visible, isConfigureSearchActive]);

  return {
    duplicateCount: duplicateBoardSearchResults?.length ?? 0,
    hasDuplicateDetection: !!detectBoardCardConfig,
    allowConfigureConflictDetection: !!detectBoardCardConfig?.allowConfigureConflictDetection,
    showInDetection: detectBoardCardConfig?.showInDetection ?? {},
    buildDefaultSearchQuery,
    getConfigureSearchInitialQuery,
    searchBoardsForDuplicates,
  };
}
